import { supabase } from '../lib/supabaseClient';
import {
  Organization,
  OrganizationStatus,
  OrganizationSettings,
  createOrganization,
  validateOrganizationSettings,
} from '../models/Organization';

/**
 * A blocked organization write (e.g. a non-admin whose RLS policy filters the
 * row out of the UPDATE) comes back from PostgREST as `error: null` with zero
 * rows affected — so without this guard the caller reports a false success.
 * Every org mutation selects the touched ids and asserts at least one changed.
 */
function assertRowWritten(rows: unknown): void {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(
      'Organization was not updated. You may not have permission to manage this organization.',
    );
  }
}

export class OrganizationService {
  private static mapRow(row: any): Organization {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      type: row.type,
      status: row.status,
      joinCode: row.join_code,
      baseAirport: row.base_airport || {},
      contact: row.contact || {},
      createdByUserId: row.owner_uid,
      ownerUid: row.owner_uid,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      settings: row.settings || {},
      members: row.members || [],
      logo: row.logo || undefined,
      description: row.description || undefined,
      socialMedia: row.social_media || undefined,
    };
  }

  // Create a new organization
  static async createOrganization(
    id: string,
    name: string,
    type: Organization['type'],
    baseAirport: Organization['baseAirport'],
    contact: Organization['contact'],
    createdByUserId: string,
    settings: OrganizationSettings,
    options?: {
      logo?: string;
      description?: string;
      socialMedia?: Organization['socialMedia'];
    }
  ): Promise<Organization> {
    const organization = createOrganization(
      id,
      name,
      type,
      baseAirport,
      contact,
      createdByUserId,
      settings,
      options
    );

    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('organizations')
      .insert({
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
        type: organization.type,
        status: organization.status,
        join_code: organization.joinCode,
        base_airport: organization.baseAirport,
        contact: organization.contact,
        settings: organization.settings,
        owner_uid: organization.createdByUserId,
        created_at: now,
        updated_at: now,
        logo: organization.logo || null,
        description: organization.description || null,
        social_media: organization.socialMedia || null
      })
      .select('*')
      .single();

    if (error || !data) {
      throw error || new Error('Failed to create organization');
    }

    return this.mapRow(data);
  }

  // Get organization by ID
  static async getOrganization(id: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    return this.mapRow(data);
  }

  // Get organization by slug
  static async getOrganizationBySlug(slug: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    return this.mapRow(data);
  }

  // Get organizations by owner
  static async getOrganizationsByOwner(ownerUid: string): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('owner_uid', ownerUid)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return (data || []).map(row => this.mapRow(row));
  }

  // Get single organization by owner
  static async getOrganizationByOwner(ownerUid: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('owner_uid', ownerUid)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    return this.mapRow(data);
  }

  // Update organization status
  static async updateOrganizationStatus(
    id: string,
    status: OrganizationStatus
  ): Promise<void> {
    const { data, error } = await supabase
      .from('organizations')
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('id');

    if (error) {
      throw error;
    }
    assertRowWritten(data);
  }

  // Update organization settings
  static async updateOrganizationSettings(
    id: string,
    settings: Partial<OrganizationSettings>
  ): Promise<void> {
    const organization = await this.getOrganization(id);
    if (!organization) {
      throw new Error('Organization not found');
    }

    const updatedSettings = {
      ...organization.settings,
      ...settings,
    };

    if (!validateOrganizationSettings(updatedSettings)) {
      throw new Error('Invalid organization settings');
    }

    const { data, error } = await supabase
      .from('organizations')
      .update({
        settings: updatedSettings,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('id');

    if (error) {
      throw error;
    }
    assertRowWritten(data);
  }

  // Update organization details
  static async updateOrganization(
    id: string,
    updates: Partial<Omit<Organization, 'id' | 'createdAt' | 'updatedAt' | 'createdByUserId' | 'ownerUid'>>
  ): Promise<void> {
    const payload: any = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.slug !== undefined) payload.slug = updates.slug;
    if (updates.type !== undefined) payload.type = updates.type;
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.joinCode !== undefined) payload.join_code = updates.joinCode;
    if (updates.baseAirport !== undefined) payload.base_airport = updates.baseAirport;
    if (updates.contact !== undefined) payload.contact = updates.contact;
    if (updates.settings !== undefined) payload.settings = updates.settings;
    if (updates.logo !== undefined) payload.logo = updates.logo;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.socialMedia !== undefined) payload.social_media = updates.socialMedia;

    const { data, error } = await supabase
      .from('organizations')
      .update(payload)
      .eq('id', id)
      .select('id');

    if (error) {
      throw error;
    }
    assertRowWritten(data);
  }

  // Delete organization
  static async deleteOrganization(id: string): Promise<void> {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  }

  // Search organizations
  static async searchOrganizations(
    searchTerm: string,
    limitCount: number = 10
  ): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .ilike('name', `%${searchTerm}%`)
      .limit(limitCount);

    if (error) {
      throw error;
    }

    return (data || []).map(row => this.mapRow(row));
  }

  // Get active organizations
  static async getActiveOrganizations(limitCount: number = 10): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(limitCount);

    if (error) {
      throw error;
    }

    return (data || []).map(row => this.mapRow(row));
  }
} 